import { push as redirectTo } from 'connected-react-router';
import React from 'react';
import { useDispatch, connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import {
  Button,
  COLORS,
  Header,
  Text,
  Alert,
  FlexCenter,
  Strong,
} from '@summer/ui-components';

import { sendUserAccountRecoveryOTPNotification } from 'redux/actions/accountRecovery.actions';
import { clearServerError } from 'redux/actions/ui.actions';
import { sendAccountRecoveryEmailOtpNotificationLabel } from 'redux/middleware/accountRecovery.middleware';
import { getErrorCode } from 'redux/selectors/ui.selectors';

/**
 * @param {Object} props
 * @param {string} props.sendOTPNotificationErrorCode
 * @returns {React.Component}
 */
const AccountRecoveryStart = ({ sendOTPNotificationErrorCode }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  /**
   * @type {{ recovery: object }}
   * @property {object} recovery
   * @property {string} recovery.obfuscatedEmail
   * @property {string} recovery.obfuscatedPhone
   */
  const { recovery } = location.state;

  const formatError = sendOTPNotificationErrorCode => {
    const errorMap = new Map([
      ['429', 'Too many requests. Please try again later.'],
    ]);

    return (
      errorMap.get(sendOTPNotificationErrorCode.toString()) ||
      'Something went wrong. Please try again.'
    );
  };

  const handleGetStarted = () => {
    dispatch(clearServerError(sendAccountRecoveryEmailOtpNotificationLabel));
    dispatch(sendUserAccountRecoveryOTPNotification());
    dispatch(redirectTo('/account-recovery/email/verify', { recovery }));
  };

  return (
    <Container>
      <KeyIconContainer>
        <KeyImage
          src="/images/login/account-recovery-key.png"
          alt="Recovery Key"
        />
      </KeyIconContainer>

      <HeaderBox>
        <Header center>Account Update Needed</Header>
      </HeaderBox>

      <DescriptionText center>
        Recover your Summer account to continue to access your educational
        benefits
      </DescriptionText>

      <GetStartedButton onClick={handleGetStarted} width={360}>
        Get Started
      </GetStartedButton>

      {sendOTPNotificationErrorCode && (
        <ErrorAlert theme="warning" testId="invalid-code-alert-text">
          <InfoRow>
            <Text>{formatError(sendOTPNotificationErrorCode)}</Text>
          </InfoRow>
        </ErrorAlert>
      )}
      <InfoAlert dismissable={false} showIcon={false}>
        <InfoRow>
          <LightBulbIcon>💡</LightBulbIcon>
          <Text>
            <Strong>Why am I seeing this?</Strong> It looks like your
            eligibility for Summer educational benefits has changed. Please
            follow the steps above or contact your HR benefits manager if you
            think there&apos;s been an error.
          </Text>
        </InfoRow>
      </InfoAlert>
    </Container>
  );
};

const Container = styled(FlexCenter)`
  max-width: 408px;
  width: 100%;
  padding: 0 24px 72px;
  margin-top: 48px;
  flex-shrink: 0;
  flex-direction: column;
`;

const KeyIconContainer = styled.div`
  position: relative;
  width: 160px;
  height: 160px;
  background-color: ${COLORS.lighterAzure};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const KeyImage = styled.img`
  width: 80px;
  height: auto;
`;

const HeaderBox = styled.div`
  margin-bottom: 16px;
`;

const DescriptionText = styled(Text)`
  text-align: center;
  margin-bottom: 32px;
  font-size: 16px;
`;

const GetStartedButton = styled(Button)`
  margin-bottom: 16px;
`;

const ErrorAlert = styled(Alert)`
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 16px;
`;

const InfoAlert = styled(Alert)`
  background-color: ${COLORS.lighterAzure};
`;

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const LightBulbIcon = styled.span`
  font-size: 20px;
`;

const mapStateToProps = state => ({
  sendOTPNotificationErrorCode: getErrorCode(
    state,
    sendAccountRecoveryEmailOtpNotificationLabel,
  ),
});

export default connect(mapStateToProps)(AccountRecoveryStart);
